import { useCallback, useEffect, useMemo } from 'react';
import { ContentsApiResponseResultsType } from '~/searchApi';

import { usePageBuilderStore } from '../../hooks/usePageBuilderStore';
import { useAddContentStore } from '../../sidebar/useAddContentStore';
import { TalentContentProperties } from './TalentContent';

// Helper function to extract only the needed fields
const extractContentFields = (content: ContentsApiResponseResultsType) => ({
  assets: content.assets,
  creator: content.creator,
  id: content.id,
  platform: content.platform,
  content_type: content.content_type,
  status: content.status,
});

export const useContentSelection = () => {
  const {
    isEditMode,
    blockId,
    callback,
    reset,
    selectedContent,
    setSelectedContent,
    modifiedContents,
    setModifiedContents,
    showInShared,
    setShowInShared,
  } = useAddContentStore();
  const { blocks } = usePageBuilderStore();

  const talentContentBlock = isEditMode
    ? blocks.find((b) => b.component === 'TalentContent' && b.id === blockId)
    : undefined;

  // Set showInShared from block properties if it's true
  useEffect(() => {
    if (talentContentBlock?.properties) {
      const showInSharedValue = (
        talentContentBlock.properties as TalentContentProperties
      ).showInShared;
      setShowInShared(showInSharedValue);
    } else {
      // Reset to false when not in edit mode or when block properties are undefined
      setShowInShared(false);
    }
  }, [talentContentBlock, setShowInShared]);

  // Get the existing contents from the block (if any)
  const existingContents = useMemo(() => {
    if (!talentContentBlock) return [];
    return [
      ...(talentContentBlock.properties as TalentContentProperties)
        .customContent,
    ];
  }, [talentContentBlock]);

  // If the user has modified the selection, use that; otherwise fall back to existing contents.
  const baseContents = useMemo(
    () => (modifiedContents !== null ? modifiedContents : existingContents),
    [modifiedContents, existingContents],
  );

  const handleSelectContent = useCallback(
    (checked: boolean, content: ContentsApiResponseResultsType) => {
      // If no modification has been made yet, initialize modifiedContents with existing contents
      if (modifiedContents === null) {
        setModifiedContents(existingContents);
      }

      // Get the current contents, ensuring it's always an array
      const currentContents =
        modifiedContents !== null ? modifiedContents : existingContents;

      if (checked) {
        // Add the content if it isn't already there.
        if (!currentContents.some((c) => c.id === content.id)) {
          const updatedContents = [...currentContents, content];
          setModifiedContents(updatedContents);
          setSelectedContent([...selectedContent, content]);
        }
      } else {
        // Remove the content from the list.
        const updatedContents = currentContents.filter(
          (c) => c.id !== content.id,
        );
        setModifiedContents(updatedContents);
        setSelectedContent(selectedContent.filter((c) => c.id !== content.id));
      }
    },
    [
      modifiedContents,
      existingContents,
      setModifiedContents,
      setSelectedContent,
      selectedContent,
    ],
  );

  const handleClose = useCallback(() => {
    setSelectedContent([]);
    setModifiedContents(null);
  }, [setSelectedContent, setModifiedContents]);

  const handleAddEditContent = useCallback(() => {
    const finalContents =
      modifiedContents !== null ? modifiedContents : existingContents;

    if (typeof callback === 'function') {
      // Extract only the needed fields when calling the callback
      const selectedFieldsContents = finalContents.map(extractContentFields);
      callback(
        selectedFieldsContents as TalentContentProperties['customContent'],
        showInShared,
      );
    }
    reset();
  }, [modifiedContents, existingContents, callback, reset, showInShared]);

  const getIsChecked = useCallback(
    (contentId: string) => {
      const currentContents =
        modifiedContents !== null ? modifiedContents : existingContents;
      return currentContents.some((content) => content.id === contentId);
    },
    [modifiedContents, existingContents],
  );

  return {
    selectedContent,
    modifiedContents:
      modifiedContents !== null ? modifiedContents : existingContents,
    handleSelectContent,
    handleClose,
    handleAddEditContent,
    getIsChecked,
    baseContents,
  };
};
